import React from 'react';
import { Button, Text, View } from "../../../src/Components/Themed"
import type { BestAvailableOrderProps, EventProps, LeagueProps, MarketProps, OrderProps, TradeProps } from '../../../src/types';
import { useColors } from '../../../src/constants/useColors';
import { Image, StyleSheet, TouchableOpacity, useColorScheme } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import moment from 'moment-mini';
import { MarketComponentHelpers } from '../../../src/MarketComponents/api';
import { MarketMaketHelpers } from '../../../src/MarketMaker/api';
import FreeBonusBanner from './FreeBonusBanner';

type FeaturedEventCardProps = {
    event: EventProps,
    league?: LeagueProps,
    market?: MarketProps,
    trades?: TradeProps[],
    available_orders?: BestAvailableOrderProps[],
    onPress?: () => void,
    onBuyOrder?: (order:OrderProps) => void,
    showBonusBanner?: boolean,
    onClaimBonus?: () => void
}

const FeaturedEventCard = ({ event, league, market, trades, available_orders, onPress, onBuyOrder, showBonusBanner, onClaimBonus }: FeaturedEventCardProps) => {
    const Colors = useColors();
    const colorScheme = useColorScheme();

    // Prioritize available orders with show:true, fall back to latest trades
    const away_order = available_orders?.find(o =>
        o.show &&
        o.market_id == market?.market_id &&
        o.side != market?.trade_side
    );
    const home_order = available_orders?.find(o =>
        o.show &&
        o.market_id == market?.market_id &&
        o.side == market?.trade_side
    );

    // Fall back to trades if no available orders
    const away_trade = away_order || trades?.find(t => t.market_type == 'FOR_MONEY' && t.market_id == market?.market_id && t.side != market?.trade_side);
    const home_trade = home_order || trades?.find(t => t.market_type == 'FOR_MONEY' && t.market_id == market?.market_id && t.side == market?.trade_side);

    // Helper to convert best available order or trade to OrderProps
    const handleBuyClick = (data: BestAvailableOrderProps | TradeProps | undefined) => {
        console.log('handleBuyClick called', { data, market, onBuyOrder, onPress });

        // If onBuyOrder is provided, convert and call it
        if (onBuyOrder && data && market) {
            
            // Need to get reversed_side_id for the trade conversion
            const side_option = market.side_options.find(so => so.side == ('side' in data ? data.side : ''));
            if (!side_option) {
                return;
            }

            const side_id_response = MarketMaketHelpers.getTeamEventSideIds(event, market, side_option);
            if (!side_id_response) {
                return;
            }

            const reversed_side_id = side_id_response.reversed_side_id;
            if (!reversed_side_id) {
                return;
            }

            let order: OrderProps;

            // Check if it's a BestAvailableOrderProps
            if ('order_id' in data && 'open_amt' in data) {
                // Convert BA order -> trade -> order
                const trade = MarketMaketHelpers.getTradeFromAvailableOrder(data as BestAvailableOrderProps, reversed_side_id);
                order = MarketMaketHelpers.getOrderFromTrade(trade, 10, data.order_id);
            } else {
                // Convert trade -> order
                order = MarketMaketHelpers.getOrderFromTrade(data as TradeProps, 10);
            }

            // Add title
            const title = MarketComponentHelpers.getOrderTitle(order, market, event);
            if (!title) {
                return;
            }

            onBuyOrder({ ...order, title });
        } else if (onPress) {
            // Fallback to onPress if onBuyOrder not provided
            onPress();
        } else {
        }
    };

    // Create gradient colors that work with text contrast
    const gradientColors = colorScheme === 'dark'
        ? [Colors.views.header, Colors.views.body]  // Dark mode: use existing
        : ['#F0F4FF', '#E8F0FF'];  // Light mode: very light blue gradient

    return (
        <View style={styles.container}>
            <LinearGradient
                colors={gradientColors}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.gradient}
            >
                <View transparent style={styles.card}>
                        {/* Bonus Banner */}
                        {showBonusBanner && (
                            <FreeBonusBanner
                                amount="$10"
                                message="FREE to place your first bet!"
                                ctaText="Claim Now"
                                onPress={onClaimBonus}
                            />
                        )}
                        {/* Main Content - Team Logos and VS */}
                        <View transparent style={styles.mainContent}>
                            {/* Away Team */}
                            <View transparent style={styles.teamContainer}>
                                <Image
                                    source={{ uri: event.away?.image?.url }}
                                    style={styles.teamLogo}
                                    resizeMode='contain'
                                />
                                <Text theme='h1' size={16} textAlign='center' style={styles.teamName}>
                                    {event.away?.market_name}
                                </Text>
                                <Text theme='h2' size={14} textAlign='center' style={styles.teamSubName}>
                                    {event.away?.name}
                                </Text>
                                {event.away_sub_title && (
                                    <Text theme='description' size={11} textAlign='center' style={{ marginTop: 4 }}>
                                        {event.away_sub_title}
                                    </Text>
                                )}
                                {/* Away Buy Button */}
                                {away_trade && market && (
                                    <TouchableOpacity
                                        style={styles.buyButtonContainer}
                                        onPress={() => handleBuyClick(away_trade)}
                                        activeOpacity={0.8}
                                    >
                                        <LinearGradient
                                            colors={[Colors.absolutes.brand.electric, Colors.absolutes.brand.electricDark || '#0066CC']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.buyButton}
                                        >
                                            <View transparent style={styles.buyButtonContent}>
                                                <Text theme='h2' size={12} color={Colors.text.white} style={styles.buyText}>
                                                    BUY
                                                </Text>
                                                <View transparent style={styles.oddsRow}>
                                                    {market.var_1_required && (
                                                        <Text theme='h1' size={16} color={Colors.text.white} style={styles.buyOddsLine}>
                                                            {MarketComponentHelpers.getVar1Label(market, away_trade.var_1)}
                                                        </Text>
                                                    )}
                                                    <Text theme='h1' size={16} color={Colors.text.white} style={styles.buyOdds}>
                                                        {MarketComponentHelpers.getOddsLabel(away_trade.odds)}
                                                    </Text>
                                                </View>
                                            </View>
                                        </LinearGradient>
                                    </TouchableOpacity>
                                )}
                            </View>

                            {/* VS Indicator */}
                            <View transparent style={styles.vsContainer}>
                                <View style={styles.vsBadge}>
                                    <Text theme='h2' size={12} color={Colors.text.white}>VS</Text>
                                </View>
                            </View>

                            {/* Home Team */}
                            <View transparent style={styles.teamContainer}>
                                <Image
                                    source={{ uri: event.home?.image?.url }}
                                    style={styles.teamLogo}
                                    resizeMode='contain'
                                />
                                <Text theme='h1' size={16} textAlign='center' style={styles.teamName}>
                                    {event.home?.market_name}
                                </Text>
                                <Text theme='h2' size={14} textAlign='center' style={styles.teamSubName}>
                                    {event.home?.name}
                                </Text>
                                {event.home_sub_title && (
                                    <Text theme='description' size={11} textAlign='center' style={{ marginTop: 4 }}>
                                        {event.home_sub_title}
                                    </Text>
                                )}
                                {/* Home Buy Button */}
                                {home_trade && market && (
                                    <TouchableOpacity
                                        style={styles.buyButtonContainer}
                                        onPress={() => handleBuyClick(home_trade)}
                                        activeOpacity={0.8}
                                    >
                                        <LinearGradient
                                            colors={[Colors.absolutes.brand.electric, Colors.absolutes.brand.electricDark || '#0066CC']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.buyButton}
                                        >
                                            <View transparent style={styles.buyButtonContent}>
                                                <Text theme='h2' size={12} color={Colors.text.white} style={styles.buyText}>
                                                    BUY
                                                </Text>
                                                <View transparent style={styles.oddsRow}>
                                                    {market.var_1_required && (
                                                        <Text theme='h1' size={16} color={Colors.text.white} style={styles.buyOddsLine}>
                                                            {MarketComponentHelpers.getVar1Label(market, home_trade.var_1)}
                                                        </Text>
                                                    )}
                                                    <Text theme='h1' size={16} color={Colors.text.white} style={styles.buyOdds}>
                                                        {MarketComponentHelpers.getOddsLabel(home_trade.odds)}
                                                    </Text>
                                                </View>
                                            </View>
                                        </LinearGradient>
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>

                        {/* Footer - League and Time */}
                        <View transparent style={styles.footer}>
                            <View transparent style={styles.footerLeft}>
                                {league && (
                                    <View transparent style={styles.leagueBadge}>
                                        <Text theme='h2' size={10}>{league.league_name}</Text>
                                    </View>
                                )}
                                <Text theme='description' size={10}>
                                    {['created', 'scheduled'].includes(event.time_detail ?? '')
                                        ? moment(event.scheduled_datetime).format('ddd, MMM D @ h:mm A')
                                        : event.time_detail
                                    }
                                </Text>
                            </View>
                            {onPress && (
                                <TouchableOpacity
                                    style={styles.seeAllButton}
                                    onPress={onPress}
                                    activeOpacity={0.7}
                                >
                                    <Text theme='h1' size={12} color={Colors.text.white}>
                                        See All Markets →
                                    </Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                </LinearGradient>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    gradient: {
        flex: 1,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 5,
    },
    card: {
        flex: 1,
        padding: 20,
        justifyContent: 'space-between',
    },
    leagueBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        backgroundColor: 'rgba(128,128,128,0.2)',
    },
    mainContent: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingVertical: 20,
    },
    teamContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    teamLogo: {
        width: 64,
        height: 64,
        marginBottom: 12,
    },
    teamName: {
        marginTop: 4,
    },
    teamSubName: {
        marginTop: 2,
    },
    oddsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
    },
    vsContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 10,
    },
    vsBadge: {
        backgroundColor: 'rgba(128,128,128,0.3)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: 'rgba(128,128,128,0.2)',
    },
    footerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flex: 1,
    },
    seeAllButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6,
        backgroundColor: 'rgba(128,128,128,0.3)',
    },
    buyButtonContainer: {
        marginTop: 12,
        width: '100%',
        maxWidth: 140,
    },
    buyButton: {
        borderRadius: 12,
        paddingVertical: 10,
        paddingHorizontal: 16,
        shadowColor: '#0066FF',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 8,
        elevation: 8,
    },
    buyButtonContent: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    buyText: {
        fontWeight: '800',
        letterSpacing: 1.2,
        marginBottom: 4,
    },
    oddsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    buyOddsLine: {
        fontWeight: '700',
        marginRight: 6,
    },
    buyOdds: {
        fontWeight: '700',
    },
});

export default FeaturedEventCard;
